import { useState } from 'react';
import { TriangleAlert } from 'lucide-react';
import { getErrorMessage, hasProblemType, RESEND_PROBLEM_TYPES } from '@blimo/api-client';
import { Alert, AlertDescription, AlertTitle, Button, Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@blimo/ui';

/**
 * Confirmation for every sending action. If the API answers 409 `already-sent` / `already-dispatched`,
 * the dialog explains it and offers an explicit „Erneut senden“, which repeats the call with `resend: true`.
 * The decision whether something was already sent is made by the API only.
 *
 * @param {{
 *   open: boolean,
 *   onOpenChange: (open: boolean) => void,
 *   title: string,
 *   description?: import('react').ReactNode,
 *   confirmLabel?: string,
 *   send: (options: { resend: boolean }) => Promise<any>,
 *   onSent?: (result: any, options: { resend: boolean }) => void,
 *   confirmDisabled?: boolean,
 *   children?: import('react').ReactNode,
 * }} props
 */
export function SendDialog({ open, onOpenChange, title, description, confirmLabel = 'Senden', send, onSent, confirmDisabled = false, children }) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        {open && (
          <SendDialogBody
            title={title}
            description={description}
            confirmLabel={confirmLabel}
            send={send}
            onSent={onSent}
            confirmDisabled={confirmDisabled}
            onClose={() => onOpenChange(false)}
          >
            {children}
          </SendDialogBody>
        )}
      </DialogContent>
    </Dialog>
  );
}

function SendDialogBody({ title, description, confirmLabel, send, onSent, confirmDisabled, onClose, children }) {
  const [resendProblem, setResendProblem] = useState(/** @type {any} */ (null));
  const [error, setError] = useState(/** @type {string|null} */ (null));
  const [pending, setPending] = useState(false);

  async function run(resend) {
    setPending(true);
    setError(null);
    try {
      const result = await send({ resend });
      onSent?.(result, { resend });
      onClose();
    } catch (e) {
      if (!resend && hasProblemType(e, RESEND_PROBLEM_TYPES)) setResendProblem(e);
      else setError(getErrorMessage(e, 'Der Versand ist fehlgeschlagen.'));
    } finally {
      setPending(false);
    }
  }

  return (
    <>
      <DialogHeader>
        <DialogTitle>{title}</DialogTitle>
        <DialogDescription>{description ?? 'Bitte bestätigen Sie den Versand.'}</DialogDescription>
      </DialogHeader>
      {children}
      {resendProblem && (
        <Alert variant="warning" role="alert">
          <TriangleAlert aria-hidden="true" />
          <div className="flex flex-col gap-1">
            <AlertTitle>Bereits versendet</AlertTitle>
            <AlertDescription>{resendProblem.detail || 'Diese Nachricht wurde bereits versendet.'} Soll sie wirklich erneut gesendet werden?</AlertDescription>
          </div>
        </Alert>
      )}
      {error && (
        <Alert variant="destructive">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}
      <DialogFooter>
        <Button type="button" variant="outline" onClick={onClose} disabled={pending}>
          Abbrechen
        </Button>
        {resendProblem ? (
          <Button type="button" variant="destructive" loading={pending} onClick={() => run(true)}>
            Erneut senden
          </Button>
        ) : (
          <Button type="button" loading={pending} disabled={confirmDisabled} onClick={() => run(false)}>
            {confirmLabel}
          </Button>
        )}
      </DialogFooter>
    </>
  );
}
